import { Show } from "@refinedev/antd";
import { CanAccess, useCan, useNotification, useOne } from "@refinedev/core";
import {
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Row,
  Space,
  Statistic,
  Table,
  Tabs,
  Tag,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";

import { api } from "../api";
import {
  DATE_FORMAT,
  DATE_TIME_FORMAT,
  SCHOOL_GRADES,
  SUBSCRIPTION_STATUS,
  planLabel,
} from "../labels";
import type { StudentDetail } from "../types";
import { LessonsTable } from "./lessons-table";

type Contact = {
  phoneNumber: string | null;
  parentName: string | null;
  parentPhone: string | null;
};

/**
 * /students/:id (permission `students.view`): profile, plan and hours; "Dersler" with
 * `lessons.view`. Phone and parent details open with `students.contact`, audited.
 */
export function StudentShowPage() {
  const { id = "" } = useParams();
  const { open } = useNotification();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") ?? "profile";
  const { result: student, query } = useOne<StudentDetail>({ resource: "students", id });
  const { data: canSeeLessons } = useCan({ resource: "lessons", action: "list" });
  const { data: canSeeTutors } = useCan({ resource: "tutors", action: "list" });
  const [contact, setContact] = useState<Contact | null>(null);
  const [busy, setBusy] = useState(false);

  async function revealContact() {
    setBusy(true);
    try {
      setContact(await api<Contact>(`/api/office/students/${id}/contact`, { method: "POST" }));
    } catch (error) {
      open?.({ type: "error", message: error instanceof Error ? error.message : "Hata oluştu." });
    } finally {
      setBusy(false);
    }
  }

  const hours = student?.hours;
  const profile = student ? (
    <Row gutter={[24, 24]}>
      <Col xs={24} lg={12}>
        <Space direction="vertical" size={24} style={{ width: "100%" }}>
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="E-posta">
              {student.email}{" "}
              {student.emailVerified ? (
                <Tag color="green">Doğrulandı</Tag>
              ) : (
                <Tag>Doğrulanmadı</Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Sınıf">
              {SCHOOL_GRADES[student.schoolGrade] ?? student.schoolGrade}
            </Descriptions.Item>
            <Descriptions.Item label="18 yaş">
              {student.age === "ADULT" ? (
                "18 yaşından büyük"
              ) : student.age === "MINOR" ? (
                <Tag color="orange">18 yaşından küçük</Tag>
              ) : (
                <Typography.Text type="secondary">
                  Doğum tarihini henüz girmedi (ilk ödemede istenir)
                </Typography.Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Kayıt">
              {dayjs(student.createdAt).format(DATE_TIME_FORMAT)}
            </Descriptions.Item>
            <Descriptions.Item label="Haftalık döngü">
              {/* Starts at the package purchase, rounded up to the hour (2026-10-12). */}
              {student.hours
                ? `Her ${dayjs(student.hours.resetsAt).format("dddd HH:mm")} yenilenir`
                : "Aktif paket yok"}
            </Descriptions.Item>
            {student.tutorProfileId ? (
              <Descriptions.Item label="Eğitmen hesabı">
                {canSeeTutors?.can ? (
                  <Link to={`/tutors/${student.tutorProfileId}`}>Eğitmen profilini aç</Link>
                ) : (
                  "Var"
                )}
              </Descriptions.Item>
            ) : null}
          </Descriptions>

          <Card size="small" title="İletişim">
            {contact ? (
              <Descriptions column={1} size="small" data-testid="student-contact">
                <Descriptions.Item label="Telefon">{contact.phoneNumber ?? "—"}</Descriptions.Item>
                <Descriptions.Item label="Veli">{contact.parentName ?? "—"}</Descriptions.Item>
                <Descriptions.Item label="Veli telefonu">
                  {contact.parentPhone ?? "—"}
                </Descriptions.Item>
              </Descriptions>
            ) : (
              <>
                <Typography.Paragraph type="secondary">
                  Telefon{" "}
                  {student.hasPhone
                    ? student.phoneNumberVerified
                      ? "(doğrulanmış)"
                      : "(doğrulanmamış)"
                    : "yok"}
                  {student.hasParent ? " · veli bilgisi var" : ""}. Bilgiler gizli; açılması
                  Kayıtlar'a yazılır.
                </Typography.Paragraph>
                <CanAccess resource="students" action="contact">
                  <Button onClick={revealContact} loading={busy}>
                    İletişim bilgilerini göster
                  </Button>
                </CanAccess>
              </>
            )}
          </Card>
        </Space>
      </Col>

      <Col xs={24} lg={12}>
        <Space direction="vertical" size={24} style={{ width: "100%" }}>
          <Row gutter={[16, 16]}>
            <Col xs={12}>
              <Card size="small">
                <Statistic
                  title="Bu hafta kalan saat"
                  value={hours ? `${hours.remainingThisWeek} / ${hours.weeklyHours}` : "—"}
                />
              </Card>
            </Col>
            <Col xs={12}>
              <Card size="small">
                <Statistic title="Ek kredi (saat)" value={student.extraCredits} />
              </Card>
            </Col>
            <Col xs={8}>
              <Card size="small">
                <Statistic title="Tamamlanan" value={student.counts.completed} />
              </Card>
            </Col>
            <Col xs={8}>
              <Card size="small">
                <Statistic title="İptal" value={student.counts.cancelled} />
              </Card>
            </Col>
            <Col xs={8}>
              <Card size="small">
                <Statistic title="Yorum" value={student.counts.reviews} />
              </Card>
            </Col>
          </Row>
          {hours ? (
            <Typography.Text type="secondary">
              Haftalık saatler {dayjs(hours.resetsAt).format(DATE_TIME_FORMAT)} tarihinde yenilenir.
            </Typography.Text>
          ) : null}

          <Card size="small" title="Planlar">
            {student.subscriptions.length > 0 ? (
              <Table
                size="small"
                rowKey="id"
                pagination={false}
                dataSource={student.subscriptions}
                scroll={{ x: "max-content" }}
                columns={[
                  {
                    title: "Plan",
                    dataIndex: "plan",
                    render: (plan: StudentDetail["subscriptions"][number]["plan"]) =>
                      planLabel(plan),
                  },
                  {
                    title: "Durum",
                    dataIndex: "status",
                    render: (value: string) => (
                      <Tag color={SUBSCRIPTION_STATUS[value]?.color}>
                        {SUBSCRIPTION_STATUS[value]?.label}
                      </Tag>
                    ),
                  },
                  {
                    title: "Dönem",
                    key: "period",
                    render: (_: unknown, subscription: StudentDetail["subscriptions"][number]) =>
                      `${dayjs(subscription.currentPeriodStart).format(DATE_FORMAT)} – ${dayjs(subscription.currentPeriodEnd).format(DATE_FORMAT)}`,
                  },
                  {
                    title: "Yenileme",
                    key: "renew",
                    render: (_: unknown, subscription: StudentDetail["subscriptions"][number]) =>
                      subscription.autoRenew
                        ? subscription.nextPlan
                          ? `Açık · sonra ${planLabel(subscription.nextPlan)}`
                          : "Açık"
                        : "Kapalı",
                  },
                ]}
              />
            ) : (
              <Empty description="Plan satın alınmadı" />
            )}
          </Card>
        </Space>
      </Col>
    </Row>
  ) : null;

  return (
    <Show
      title={student?.name ?? "Öğrenci"}
      isLoading={query.isLoading}
      canEdit={false}
      canDelete={false}
    >
      {student ? (
        <Tabs
          activeKey={tab}
          onChange={(key) =>
            setSearchParams(key === "profile" ? {} : { tab: key }, { replace: true })
          }
          items={[
            { key: "profile", label: "Profil", children: profile },
            ...(canSeeLessons?.can
              ? [
                  {
                    key: "lessons",
                    label: "Dersler",
                    children: (
                      <LessonsTable
                        endpoint={`/api/office/students/${student.id}/lessons`}
                        showTutor
                        showStudent={false}
                      />
                    ),
                  },
                ]
              : []),
          ]}
        />
      ) : null}
    </Show>
  );
}
